import { useState, useEffect, useCallback, useContext } from 'react';
import { AppSalesOrder } from '../models/AppSalesOrder';
import { AppSalesOrderService } from '../services/appSalesOrderService';
import { UserContext } from '../context/userContext';

export const useAppSalesOrders = () => {
    const [salesOrders, setSalesOrders] = useState<AppSalesOrder[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [totalSalesOrders, setTotalSalesOrders] = useState<number>(0);
    const { currentUser } = useContext(UserContext);


    const fetchSalesOrders = async () => {
        setLoading(true);
        setError(null);
        try {
            const { orders, total } = await AppSalesOrderService.getSalesOrdersPaginated(
                1,
                10,
                undefined,
                currentUser?.warehouse_hq === "1" ? undefined : currentUser?.warehouse_id || undefined
            );
            setSalesOrders(orders as any);
            setTotal(total);
        } catch (err: any) {
            setError(err.message || 'Error fetching sales order');
        } finally {
            setLoading(false);
        }
    };

    const fetchSalesOrdersPaginated = async (page: number, limit: number, search?: string) => {
        setLoading(true);
        setError(null);
        try {
            const { orders, total } = await AppSalesOrderService.getSalesOrdersPaginated(
                page,
                limit,
                search,
                currentUser?.warehouse_hq === "1" ? undefined : currentUser?.warehouse_id || undefined
            );
            setSalesOrders(orders as any);
            setTotal(total);
            return { orders, total };
        } catch (err: any) {
            setError(err.message || 'Error fetching sales order');
        } finally {
            setLoading(false);
        }
    };

    const fetchSalesOrderById = async (id: number) => {
        setLoading(true);
        setError(null);
        try {
            return await AppSalesOrderService.getSalesOrderById(id);
        } catch (err) {
            setError('Failed to fetch sales order by ID.');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const addSalesOrder = async (newSalesOrder: Omit<AppSalesOrder, 'id' | 'created_time' | 'updated_time'>) => {
        try {
            const createdSalesOrder = await AppSalesOrderService.createSalesOrder(newSalesOrder);
            setSalesOrders(prev => [...prev, createdSalesOrder]);
            return createdSalesOrder;
        } catch (err) {
            setError('Failed to add sales order');
            console.error(err);
        }
    };

    const updateSalesOrder = async (id: number, updatedSalesOrder: Omit<AppSalesOrder, 'id' | 'created_time' | 'updated_time'>) => {
        try {
            const updatedRes = await AppSalesOrderService.updateSalesOrder(id, updatedSalesOrder);
            setSalesOrders(prev => prev.map(order => (order.id === BigInt(id) ? updatedRes : order)));
            return updatedRes;
        } catch (err) {
            setError('Failed to update sales order');
            console.error(err);
        }
    };

    const deleteSalesOrder = async (id: number) => {
        try {
            await AppSalesOrderService.deleteSalesOrder(id);
            setSalesOrders(prev => prev.filter(order => order.id !== BigInt(id)));
        } catch (err) {
            setError('Failed to delete sales order');
            console.error(err);
        }
    };

    useEffect(() => {
        fetchSalesOrders();
    }, [currentUser]);

    return {
        salesOrders,
        total,
        loading,
        error,
        fetchSalesOrders,
        fetchSalesOrdersPaginated,
        fetchSalesOrderById,
        addSalesOrder,
        updateSalesOrder,
        deleteSalesOrder
    };
};
